import { set } from 'lodash/fp';
import styled from 'styled-components';

import EditRulesBlock from 'dashboard/apps/DashboardLayoutEditor/CaseConditionalLogicEditor/EditRulesBlock';
import {
  createOperatorsMap,
  validateNonEmptyRule,
} from 'dashboard/apps/DashboardLayoutEditor/CaseConditionalLogicEditor/utils';
import Divider from 'dashboard/components/Divider';
import LogicBlock from 'dashboard/components-lib/LogicBlock';
import MaximizablePanel from 'dashboard/components-lib/MaximizablePanel';
import { CaseTemplateConditional } from 'dashboard/lib/case-layouts/computed-properties';
import { ConditionalRule } from 'dashboard/lib/workflows';
import useToggle from 'lib/hooks/useToggle';
import { JsonSchema } from 'lib/json-schema';

export const DEFAULT_MODULE_VISIBILITY_CONDITIONAL: CaseTemplateConditional = {
  rules: {
    or: [
      {
        and: [{}],
      },
    ],
  },
};

export interface CaseConditionalLogicEditorProps {
  conditional: CaseTemplateConditional;
  jsonSchema: JsonSchema;
  keyPrefix: string;
  setConditionalValidity: (valid: boolean) => void;
  updateConditional: (conditional: CaseTemplateConditional) => void;
}

const PREFIX_RULES_TEXT = 'Show if';

const ConditionalBlock = styled.div`
  cursor: pointer;
`;

const StyledMaximizablePanel = styled(MaximizablePanel)`
  min-width: 900px;
`;

const StyledDivider = styled(Divider)`
  margin-top: 0;
`;

const CaseConditionalLogicEditor = ({
  conditional,
  jsonSchema,
  keyPrefix,
  setConditionalValidity,
  updateConditional,
}: CaseConditionalLogicEditorProps) => {
  const { rules } = conditional;
  const [
    editConditionalModal,
    { enable: showEditConditionalModal, disable: hideEditConditionalModal },
  ] = useToggle(false);

  const updateRule = (orIndex: number, andIndex: number) => (rule: ConditionalRule) => {
    const newConditional = set(['rules', 'or', orIndex, 'and', andIndex], rule, conditional);
    updateConditional(newConditional);
  };

  const deleteRule = (orIndex: number, andIndex: number) => {
    const newOrRule = [
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      ...conditional.rules.or[orIndex].and.slice(0, andIndex),
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      ...conditional.rules.or[orIndex].and.slice(andIndex + 1),
    ];

    let newConditional;
    // if last and rule, remove the or rule itself
    if (newOrRule.length === 0) {
      newConditional = set(
        ['rules', 'or'],
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        [...conditional.rules.or.slice(0, orIndex), ...conditional.rules.or.slice(orIndex + 1)],
        conditional,
      );
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      if (newConditional.rules.or.length === 0) {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        newConditional.rules.or = [{ and: [{}] }];
      }
    } else {
      newConditional = set(['rules', 'or', orIndex, 'and'], newOrRule, conditional);
    }
    updateConditional(newConditional);
  };

  const addAndRule = (orIndex: number) => {
    const newConditional = set(
      ['rules', 'or', orIndex, 'and'],
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      conditional.rules.or[orIndex].and.concat({}),
      conditional,
    );
    updateConditional(newConditional);
  };

  const addOrRule = () => {
    const newConditional = set(
      ['rules', 'or'],
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      conditional.rules.or.concat({ and: [{}] }),
      conditional,
    );
    updateConditional(newConditional);
  };

  const operatorsMap = createOperatorsMap();
  return (
    <ConditionalBlock onClick={showEditConditionalModal}>
      {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
      {rules.or.map((rule, orIndex) => {
        // for Every Or rules block, start the block with "Show if" if it is the first rule
        // or "Or" for every following rule block
        //
        // Show if   LogicBlock
        // Or        LogicBlock
        // Or        LogicBlock
        const prefixText = orIndex === 0 ? PREFIX_RULES_TEXT : 'Or';
        return (
          <LogicBlock
            andRules={rule}
            prefixText={prefixText}
            keyPrefix={keyPrefix + '_view'}
            orIndex={orIndex}
            key={`${keyPrefix}_view_${orIndex}_LogicBlock`}
            validateRule={validateNonEmptyRule}
          />
        );
      })}
      {editConditionalModal && (
        <StyledMaximizablePanel
          title='Visibility Configuration'
          offsetElementId='control-panel'
          onOutsideClickAndEscPress={hideEditConditionalModal}
          outsideExceptionalIdPrefixes={['edit-rule-input-possibilities']}
        >
          <StyledDivider full />
          <EditRulesBlock
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            rules={rules}
            jsonSchema={jsonSchema}
            prefixText={PREFIX_RULES_TEXT}
            editable={true}
            enableFieldValueDropdown={true}
            enableValidation={true}
            operatorsMap={operatorsMap}
            validateRule={validateNonEmptyRule}
            keyPrefix={keyPrefix + '_edit'}
            addAndRule={addAndRule}
            addOrRule={addOrRule}
            updateRule={updateRule}
            deleteRule={deleteRule}
            setConditionalValidity={setConditionalValidity}
          />
        </StyledMaximizablePanel>
      )}
    </ConditionalBlock>
  );
};

export default CaseConditionalLogicEditor;
